import * as Sentry from "@client/telemetry/sentry";
import type { IncomingUnconfirmedActionMessage } from "@engine/core";
import { isTransientIndexedDbDisconnectError } from "@engine/storageAdapters/indexedDbErrors";

export type QueuedEdit = {
  createdAt: number;
  message: IncomingUnconfirmedActionMessage;
};

/**
 * Settle a display surface's read of the queued-edit list.
 *
 * The list is read from IndexedDB, which can fail transiently on iOS (the
 * connection drops, or the request was in flight when the process froze).
 * Every surface that shows the list must treat a failed read as "unknown",
 * never as "zero pending edits" (a lie that hides a queued edit) and never as
 * an unhandled rejection. Resolves to `null` on any failure; a transient one
 * only warns, anything else is reported.
 */
export async function readQueuedEditsForDisplay(
  read: Promise<QueuedEdit[]>,
  surface: string,
): Promise<QueuedEdit[] | null> {
  try {
    return await read;
  } catch (error) {
    if (isTransientIndexedDbDisconnectError(error)) {
      console.warn(
        `[action-sync][${surface}] transient IndexedDB failure while reading queued edits`,
        { error },
      );
      return null;
    }

    Sentry.captureException(error);
    return null;
  }
}
